"use client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import Image from "next/image";
import { ReactNode } from "react";
import aboutus from "@/public/images/aboutus.jpg";
import logo from "@/public/images/smallLogo.png";
import star from "@/public/images/starBlack.svg";
import time from "@/public/images/time.svg";
import user from "@/public/images/user.svg";
import trueCircle from "@/public/images/trueCircle.svg";
import { Button } from "@/components/ui/button";
import { DialogClose } from "@radix-ui/react-dialog";
import SettTime from "./SettTime";
import { useTranslate } from "@/components/base/providers/TranslateProv";
import { useGetPaidConsultationSettingQuery } from "@/redux/features/api/Consultation/consultationApiSlice";

interface Props {
  children: ReactNode;
}

const PaidConsultation = ({ children }: Props) => {
  const { locale } = useTranslate(); // Get the current locale

  const isArabic = locale === "ar";
  const { data } = useGetPaidConsultationSettingQuery({});
  return (
    <>
      <Dialog>
        <DialogTrigger>{children}</DialogTrigger>
        <DialogContent className="overflow-y-auto overflow-x-hidden max-h-screen z-[1012]">
          <DialogHeader>
            <div className="relative mx-auto">
              <Image
                src={aboutus}
                alt={isArabic ? "عننا" : "About Us"}
                className="rounded-full w-28 h-28 object-cover"
              />
              <Badge className="absolute -top-4 -right-[8px] bg-transparent">
                <Image
                  src={logo}
                  alt={isArabic ? "شعار" : "Logo"}
                  className="w-9"
                />
              </Badge>
            </div>
            <DialogDescription>
              <div className="my-2">
                <h2 className="text-darkText text-center font-semibold text-[18px] leading-[150%]">
                  {isArabic
                    ? "احجز استشاره مع “سفير الجامعات”"
                    : "Book a Consultation with 'University Ambassador'"}
                </h2>
                <p className="text-darkText text-center font-medium text-[14px] leading-[200%] mt-2">
                  {isArabic
                    ? "حدد الحاجات الـ محتجها في الاستشاره الخاصه بيك لان بناء عليها بيتم تحديد السعر"
                    : "Specify your consultation needs as the price is determined accordingly."}
                </p>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div className=" bg-[#F8F9FA] rounded-[10px] p-2 flex flex-col items-center justify-center gap-3">
                    <Image
                      src={star}
                      alt={isArabic ? "تقيم الاستشارة" : "Consultation Rating"}
                    />
                    <p className="text-13 text-darkText">
                      {isArabic ? "تقيم الاستشارة" : "Consultation Rating"}
                    </p>
                    <p className="text-13 text-[#ACB5BB] font-[600]">
                      {isArabic ? "+5/5 نجوم" : "+5/5 Stars"}
                    </p>
                  </div>
                  <div className=" bg-[#F8F9FA] rounded-[10px] p-2 flex flex-col items-center justify-center gap-3">
                    <Image src={time} alt={isArabic ? "الوقت" : "Time"} />
                    <p className="text-13 text-darkText">
                      {isArabic ? "الوقت" : "Time"}
                    </p>
                    <p className="text-13 text-[#FF4C51] font-[600]">
                      {!data ? "00:00:00" : data?.data?.duration[locale]}
                    </p>
                  </div>
                  <div className=" bg-[#F8F9FA] rounded-[10px] p-2 flex flex-col items-center justify-center gap-3">
                    <Image
                      src={user}
                      alt={
                        isArabic
                          ? "عدد طلابات الاستشارة"
                          : "Number of Consultations"
                      }
                    />
                    <p className="text-13 text-darkText">
                      {isArabic
                        ? "عدد طلابات الاستشارة"
                        : "Number of Consultations"}
                    </p>
                    <p className="text-13 text-[#ACB5BB] font-[600]">
                      {isArabic ? "+5000 شخص" : "+5000 People"}
                    </p>
                  </div>
                </div>
                <div className="border border-gray-200 rounded p-2 mt-4">
                  <h3 className="text-darkText text-center font-medium text-10 leading-normal mb-2 mx-2 border-b pb-2">
                    {isArabic
                      ? "إليك نبذة عن الأمور التي يمكنك الاستفسار عنها :"
                      : "Here are some topics you can inquire about:"}
                  </h3>
                  <ul className="m-2 border border-[#EBEBEB] bg-white rounded-lg shadow-lg p-2">
                    <li className="flex items-center my-2">
                      <Image
                        src={trueCircle}
                        alt={isArabic ? "true circle" : "check"}
                        className="ml-2"
                      />
                      <p className="text-darkText text-[13px]">
                        {isArabic
                          ? "التعريف بكافة الجامعات الخاصة والآهلية وذات طابع الخاص في مصر"
                          : "Introduction to all private, national, and special-purpose universities in Egypt"}
                      </p>
                    </li>
                    <li className="flex items-center my-2">
                      <Image
                        src={trueCircle}
                        alt={isArabic ? "true circle" : "check"}
                        className="ml-2"
                      />
                      <p className="text-darkText text-[13px]">
                        {isArabic
                          ? "المساعدة في اختيار الجامعة المناسبة لك"
                          : "Assistance in choosing the right university for you"}
                      </p>
                    </li>
                    <li className="flex items-center my-2">
                      <Image
                        src={trueCircle}
                        alt={isArabic ? "true circle" : "check"}
                        className="ml-2"
                      />
                      <p className="text-darkText text-[13px]">
                        {isArabic
                          ? "كيفية التقديم بالجامعات المختلفة"
                          : "How to apply to different universities"}
                      </p>
                    </li>
                    <li className="flex items-center my-2">
                      <Image
                        src={trueCircle}
                        alt={isArabic ? "true circle" : "check"}
                        className="ml-2"
                      />
                      <p className="text-darkText text-[13px]">
                        {isArabic
                          ? "المساعدة في تجهيز المستندات المطلوبة في التقديم"
                          : "Assistance in preparing the required documents for application"}
                      </p>
                    </li>
                    <li className="flex items-center my-2">
                      <Image
                        src={trueCircle}
                        alt={isArabic ? "true circle" : "check"}
                        className="ml-2"
                      />
                      <p className="text-darkText text-[13px] font-semibold">
                        {isArabic
                          ? "الاستشارة ستكون عبر الهاتف والميتنج و الرسايل “واتساب”"
                          : "The consultation will be via phone, meeting, and WhatsApp messages"}
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="flex gap-5 mt-4">
                  <div className="w-full">
                    <DialogClose asChild>
                      <SettTime />
                    </DialogClose>
                  </div>
                  <div className="w-fit relative">
                    <Button className="w-fit bg-[#F8F9FA] mr-2 font-semibold text-primaryColor">
                      {!data
                        ? "السعر"
                        : ` ${data?.data?.price} ${
                            isArabic ? " جنية" : " EGP"
                          }`}
                    </Button>
                    <Badge className="absolute -top-4 -left-[8px] bg-destructive rounded-full">
                      30%
                    </Badge>
                  </div>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaidConsultation;
